import React, { useMemo, useState } from "react";
import styles from "./CdekSelector.module.scss";
import { CdekPickupPoint, CdekTariff } from "../../../../types/types";
import { useTheme } from "../../../../hooks/useTheme";
import CdekOfferCard from "./cdekOfferCard/CdekOfferCard";
import Button from "../../../button/Button";
import Search from "../../../search/Search";

interface CdekSelectorProps {
  tariffs: CdekTariff[];
  pickupPoints: CdekPickupPoint[];
  selectedTariff: CdekTariff | null;
  selectedPickupPoint: CdekPickupPoint | null;
  onSelectTariff: (tariff: CdekTariff) => void;
  onSelectPickupPoint: (point: CdekPickupPoint) => void;
}

const CdekSelector = ({
  tariffs,
  pickupPoints,
  selectedTariff,
  selectedPickupPoint,
  onSelectTariff,
  onSelectPickupPoint,
}: CdekSelectorProps) => {
  const { theme } = useTheme();
  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const courierTariffs = useMemo(() => {
    return tariffs.filter((t) => t.delivery_mode === 1);
  }, [tariffs]);

  const pickupTariffs = useMemo(() => {
    return tariffs.filter(
      (t) => t.delivery_mode === 2 || t.delivery_mode === 7,
    );
  }, [tariffs]);

  const isPickupSelected =
    selectedTariff && pickupTariffs.includes(selectedTariff);

  const filteredPickupPoints = useMemo(() => {
    if (!searchQuery.trim()) return pickupPoints;
    return pickupPoints.filter((point) =>
      point.location.address_full
        .toLowerCase()
        .includes(searchQuery.toLowerCase()),
    );
  }, [pickupPoints, searchQuery]);

  const visiblePickupPoints = useMemo(() => {
    if (!isPickupSelected) return [];
    if (showAll) return filteredPickupPoints;
    return filteredPickupPoints.slice(0, 3);
  }, [isPickupSelected, filteredPickupPoints, showAll]);

  const hasMorePoints = filteredPickupPoints.length > 3;

  return (
    <div className={styles.container}>
      <h3 className={styles.title} style={{ color: theme.text }}>
        Доставка СДЭК
      </h3>

      {/* ПВЗ (если выбран тариф с pickup) */}
      {isPickupSelected && pickupPoints.length > 0 && (
        <div className={styles.pickupSection}>
          <h4
            className={styles.subtitle}
            style={{ color: theme.textSecondary }}
          >
            Пункты выдачи
          </h4>

          {/* Поиск по адресу */}
          <Search
            value={searchQuery}
            onChange={setSearchQuery}
            clear={() => setSearchQuery("")}
            placeHolder="Поиск по адресу..."
          />

          <div className={styles.pickupList}>
            {visiblePickupPoints.map((point) => (
              <CdekOfferCard
                key={point.code}
                type="pickup"
                pointType={point.type}
                name={point.name}
                price={selectedTariff?.delivery_sum || 0}
                periodMin={selectedTariff?.period_min || 0}
                periodMax={selectedTariff?.period_max || 0}
                address={point.location.address_full}
                workTime={point.work_time}
                phone={point.phones?.[0]?.number}
                latitude={point.location.latitude}
                longitude={point.location.longitude}
                isSelected={selectedPickupPoint?.code === point.code}
                onSelect={() => onSelectPickupPoint(point)}
              />
            ))}
          </div>

          {hasMorePoints && (
            <Button
              title={
                showAll
                  ? "Скрыть"
                  : `Показать все (${filteredPickupPoints.length})`
              }
              variant="secondary"
              size="small"
              onClick={() => setShowAll(!showAll)}
              fullWidth
            />
          )}
        </div>
      )}

      {/* Курьерские тарифы */}
      <div className={styles.optionsContainer}>
        {courierTariffs.map((tariff) => (
          <CdekOfferCard
            key={tariff.tariff_code}
            type="courier"
            name={tariff.tariff_name}
            price={tariff.delivery_sum}
            periodMin={tariff.period_min}
            periodMax={tariff.period_max}
            isSelected={selectedTariff?.tariff_code === tariff.tariff_code}
            onSelect={() => onSelectTariff(tariff)}
          />
        ))}
      </div>
    </div>
  );
};

export default CdekSelector;
